<template>
  <div>
    <Modal @closeModal="$emit('closeTriggered')">
      <!-- MODAL TITLE -->
      <div slot="modal-title">
        <span class="font-weight-bold brand_navy">Schedule Live Class</span>
      </div>

      <!-- MODAL BODY -->
      <div slot="modal-body">
        <form
          action
          @submit.prevent="handleProcessingOfLiveClass"
          autocomplete="off"
        >
          <div class="new-post-input-section w-100">
            <!-- META TEXT -->
            <div class="meta-text">
              <div
                class="textarea brand_black"
                ref="liveclassTextArea"
                contenteditable="true"
                @click="clearOutPlaceholder"
                @input="updateLiveClassState"
                @paste="updateLiveClassState"
              >
                Add Instruction ...
              </div>
              <div
                class="help-text mt-2 mb-0 w-100 text-right font-11 border_grey_dark"
              >
                Max Length: 250 Characters
              </div>
            </div>

            <!-- POST INPUT BLOCK -->
            <div class="input-block white rounded-5 w-100">
              <div class="title-row">
                <div class="title-text font-weight-bold color_text">
                  New Live Class
                </div>

                <div
                  class="cancel border_grey_dark pointer"
                  @click="clearOutInputState"
                >
                  Cancel
                </div>
              </div>

              <!-- TITLE INPUT -->
              <input
                type="text"
                class="form-control border-0 color_grey_dark title-input"
                placeholder="Give your live class a name"
                v-model="form.title"
                @input="updateLiveClassState"
                required
              />

              <!-- INPUT ROW -->

              <div class="input-row mt-4">
                <!-- DATE SELECTOR -->
                <div class="input-selector date-input mr-2 mb-3 mb-sm-0">
                  <!-- AVATAR -->
                  <div class="avatar">
                    <span
                      class="icon-calendar flex-center color_grey_dark font-20 font-weight-400"
                    ></span>
                  </div>
                  <input
                    type="date"
                    class="form-control color_white_bg border-0 font-14"
                    placeholder="Live class date"
                    v-model="form.date"
                    @change="updateLiveClassState"
                  />
                  <div class="text-info ml-2">
                    <span
                      class="hint--top hint--info hint--rounded"
                      aria-label="Lesson Date"
                    >
                      <i class="icon-info brand_green pointer"></i>
                    </span>
                  </div>
                </div>

                <!-- TIME SELECTOR -->
                <div class="input-selector time-input ml-1">
                  <!-- AVATAR -->
                  <div class="avatar">
                    <span
                      class="icon-watch flex-center color_grey_dark font-20 font-weight-400"
                    ></span>
                  </div>
                  <input
                    type="time"
                    class="form-control color_white_bg border-0 font-14"
                    placeholder="Live class time"
                    v-model="form.time"
                    @change="updateLiveClassState"
                  />
                  <div class="text-info ml-2">
                    <span
                      class="hint--top hint--info hint--rounded"
                      aria-label="Lesson Time"
                    >
                      <i class="icon-info brand_green pointer"></i>
                    </span>
                  </div>
                </div>
              </div>

              <!-- WEEK SELECTOR -->
              <div class="input-selector mt-3" v-if="false">
                <!-- AVATAR -->
                <div class="avatar">
                  <span
                    class="icon-swap flex-center font-20 color_grey_dark font-weight-400"
                  ></span>
                </div>

                <!-- INPUT -->
                <select
                  id
                  @change="updateLiveClassState"
                  v-model="form.repetition"
                  required
                  class="form-control border-0 color_white_bg input-form-override"
                >
                  <option value selected disabled>Select Interval</option>
                  <option
                    v-for="option in options"
                    :key="option"
                    :value="option.toLowerCase()"
                  >
                    {{ option }}
                  </option>
                </select>
              </div>

              <!-- SUBJECT SELECTOR -->
              <div class="input-selector mt-3">
                <!-- AVATAR -->
                <div class="avatar">
                  <span
                    class="icon-book-cover flex-center font-20 color_grey_dark font-weight-400"
                  ></span>
                </div>

                <!-- INPUT -->
                <select
                  @change="updateLiveClassState"
                  v-model="form.subject_id"
                  id
                  class="form-control border-0 color_white_bg input-form-override"
                >
                  <option value selected disabled>Select Subject</option>
                  <option
                    v-for="subject in getTeacherSubjects"
                    :key="subject.id"
                    :value="subject.id"
                  >
                    {{ subject.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <!-- CANCEL OR SUBMIT FORM -->
          <div
            class="row d-flex justify-content-center align-items-center nowrap mt-4 mt-md-5"
          >
            <button
              @click="$emit('closeTriggered')"
              class="btn btn-md black-ic z-depth-0 mr-4"
            >
              Cancel
            </button>
            <template v-if="is_valid_class">
              <button
                type="submit"
                class="btn btn-md btn-accent ml-4"
                ref="scheduleBtn"
                :disabled="is_complete"
              >
                Schedule class
              </button>
            </template>
            <template v-else>
              <button
                class="btn btn-md color_text brand_accent_bg hint--primary hint-rounded hint-top disable-btn-styling ml-4"
                aria-label="Invalid class"
              >
                Schedule class
              </button>
            </template>
          </div>
        </form>
      </div>
    </Modal>

    <ResponseDialog
      v-if="response_dialog"
      :alert_msg="alert_card_msg"
      :alert_type="alert_card_type"
      @closeAlert="toggleResponseDialog"
    ></ResponseDialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { EventBus } from "@/scripts/event-bus.js";
import Modal from "@/components/globalComps/Modal";

export default {
  name: "NewLiveClassModal",

  components: {
    Modal,
    ResponseDialog: () =>
      import(
        /* webpackChunkName: "ResponseDialog" */ "@/components/globalComps/ResponseDialog"
      ),
  },

  props: {
    post_audience: String,
    process_data: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      form: {
        description: "",
        title: "",
        subject_id: "",
        repetition: "",
        availability: "",
        date: "",
        time: "00:00",
      },

      options: ["Once", "Daily", "Weekly", "Monthly"],
      placeholder_value: true,
      is_complete: true,

      alert_card_msg: null,
      alert_card_type: null,
      response_dialog: false,

      is_valid_class: false,
      account_type: this.$route.path.split("/")[1],
    };
  },

  watch: {
    getInputState: {
      handler(newVal) {
        if (Object.keys(this.getInputState).length !== 0) {
          if (newVal) {
            let new_data = Object.values(this.getInputState).filter(
              (value) => value === ""
            );
            if (new_data.length === 0) this.is_complete = false;
            else this.is_complete = true;
          }
        }
      },
    },
  },

  computed: {
    ...mapGetters(["getCurrentClass", "getInputState", "getTeacherSubjects"]),
  },

  mounted() {
    this.checkClassValidity();
    this.getTeacherSubjectsList(this.getCurrentClass);
    this.setupDateTime();
  },

  methods: {
    ...mapActions([
      "getTeacherSubjectsList",
      "setInputState",
      "createLiveClass",
      "verifyClassStatus",
    ]),

    setupDateTime() {
      let schedule = new Date();
      let year = schedule.getFullYear(),
        month =
          schedule.getMonth() + 1 > 9
            ? schedule.getMonth() + 1
            : `0${schedule.getMonth() + 1}`,
        day =
          schedule.getDate() > 9
            ? schedule.getDate()
            : `0${schedule.getDate()}`,
        hours =
          schedule.getHours() > 9
            ? schedule.getHours()
            : `0${schedule.getHours()}`,
        minutes =
          schedule.getMinutes() > 9
            ? schedule.getMinutes()
            : `0${schedule.getMinutes()}`;

      this.form.date = `${year}-${month}-${day}`;
      this.form.time = `${hours}:${minutes}`;
    },

    checkClassValidity() {
      if (this.account_type === "teacher") {
        this.verifyClassStatus(this.getCurrentClass)
          .then((response) => {
            response.data === false
              ? (this.is_valid_class = false)
              : (this.is_valid_class = true);
          })
          .catch((err) => console.log(err));
      } else this.is_valid_class = true;
    },

    clearOutPlaceholder() {
      if (this.placeholder_value === true)
        this.$refs.liveclassTextArea.innerText = "";
      this.placeholder_value = false;
    },

    toggleResponseDialog(msg = "", type = "", toggle = false) {
      this.alert_card_msg = msg;
      this.alert_card_type = type;
      this.response_dialog = toggle;
    },

    clearOutInputState() {
      this.$refs.liveclassTextArea.innerText = "Add Instruction ...";
      this.placeholder_value === true;
      Object.keys(this.form).map((input) => (this.form[input] = ""));
      this.setInputState({});
    },

    updateLiveClassState() {
      // CHECK DESCRIPTION TEXT LENGTH
      let description_text = this.$refs.liveclassTextArea.innerText.trim();
      if (description_text.length > 250)
        this.toggleResponseDialog(
          "250 maximum character length exceeded",
          "warning",
          true
        );

      this.form.availability = this.date + " " + this.time;

      this.setInputState({
        repetition: "once",
        availability: this.form.date + " " + this.form.time,
        description: description_text,
        subject_id: this.form.subject_id,
        title: this.form.title,
        view_by: "class",
        class_id: this.getCurrentClass,
      });
    },

    handleProcessingOfLiveClass() {
      let description_text = this.$refs.liveclassTextArea.innerText.trim();
      if (description_text.length < 250) {
        this.$refs.scheduleBtn.innerText = "Scheduling...";
        this.createLiveClass(this.getInputState)
          .then((response) => {
            setTimeout(() => {
              if (response.code === 200) {
                this.toggleResponseDialog(
                  "Live class created successfully!",
                  "success",
                  true
                );
                EventBus.$emit("event-bus-remount-post-list");
                this.clearOutInputState();

                setTimeout(() => {
                  this.$emit("closeTriggered");
                  this.$refs.scheduleBtn.innerText = "Schedule class";
                }, 1500);
              } else {
                this.$refs.scheduleBtn.innerText = "Schedule class";
                this.toggleResponseDialog(
                  "Please fill out all fields",
                  "warning",
                  true
                );
              }
            }, 1000);
          })
          .catch((err) => console.log(err));
      } else {
        this.toggleResponseDialog(
          "250 maximum character length exceeded",
          "warning",
          true
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.new-post-input-section {
  .meta-text {
    margin-bottom: 15px;

    .textarea {
      font-size: 14px;
      line-height: 165%;
      height: auto;
      margin-bottom: 0;
      width: 99.5%;
      position: relative;
      left: 0px;
      word-wrap: break-word;

      @include breakpoint_max(sm) {
        font-size: 13px;
      }

      @include breakpoint_max(xs) {
        font-size: 12px;
      }

      a {
        color: $brand_inverse;
        transition: all 0.3s ease-in-out;
        cursor: text;
        &:hover {
          color: $brand_accent;
        }
      }

      &:focus {
        border: 0;
        outline: 0;
      }
    }
  }

  .input-block {
    @include breakpoint_max(xs) {
      padding-left: 6px;
    }

    .title-text {
      @include breakpoint_max(xs) {
        font-size: 12.5px;
        font-weight: 600 !important;
      }
    }
    .cancel {
      @include breakpoint_max(xs) {
        font-size: 10.5px;
      }
    }

    input {
      @include breakpoint_max(sm) {
        font-size: 12px;
      }
      @include breakpoint_max(xs) {
        font-size: 11.5px;
      }
    }
  }

  .input-row {
    @include flex_row_start_nowrap;

    @include breakpoint_max(sm) {
      @include flex_column_start;
    }

    .time-input {
      @include breakpoint_max(sm) {
        position: relative;
        left: -4px;
      }
    }

    .form-control {
      font-size: 13px !important;
      width: 165px !important;

      @include breakpoint_max(lg) {
        width: 145px !important;
      }

      @include breakpoint_max(sm) {
        font-size: 12px !important;
        width: 260px !important;
      }

      @include breakpoint_max(xs) {
        font-size: 11.5px !important;
        width: 210px !important;
      }

      @include breakpoint_min_max(340, 390) {
        width: 190px !important;
      }
    }
  }

  .input-selector {
    .avatar {
      span {
        @include breakpoint_max(sm) {
          font-size: 17px !important;
        }
      }
    }

    .icon-book-cover {
      @include breakpoint_max(sm) {
        font-size: 17px !important;
      }
    }
  }

  .input-form-override {
    width: 60% !important;
    font-size: 13px !important;

    @include breakpoint_max(lg) {
      width: 100% !important;
    }

    @include breakpoint_max(sm) {
      font-size: 12px !important;
    }

    @include breakpoint_max(xs) {
      font-size: 11.5px !important;
    }
  }
}

.btn {
  @include breakpoint_max(sm) {
    font-size: 9px !important;
    padding: 10px 20px !important;
  }
}
</style>
